import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useLeadSources, useReasons } from '@/api/m3';
import type { LeadCard } from '@/api/m3-types';
import { useEvents } from '@/api/queries';
import { Option } from '@/components/kit/Controls';
import { PhoneField, TextAreaField, TextField } from '@/components/kit/Field';
import { SectionLabel } from '@/components/kit/Layout';
import { Modal } from '@/components/kit/Modal';
import { Note } from '@/components/kit/Note';
import { OptionList } from '@/components/kit/OptionList';
import { CityField, type CityValue } from '@/features/base/shared/CityField';
import { deals, eventLabel, LEAD_STATUS, fullDateTime, stageTitle } from '../labels';
import { useM3Submit } from '../useM3Submit';

/**
 * Куда квалифицируют и заводят сделки (ТЗ 6.2): черновики — продажи
 * начинают до публикации (Road Map п. 13) — и опубликованные с открытым
 * приёмом. Окончательно решает сервер.
 */
export function useOpenEvents() {
  const events = useEvents();
  return useMemo(
    () =>
      (events.data ?? [])
        .filter((e) => e.state === 'draft' || (e.state === 'live' && e.accepting))
        .sort((a, b) => a.dateFrom.localeCompare(b.dateFrom))
        .map((e) => ({ id: e.id, label: e.state === 'draft' ? `${eventLabel(e)} · черновик` : eventLabel(e) })),
    [events.data],
  );
}

/**
 * «＋ Заявка» (newLead в макете): звонок, сообщение, встреча. Клиент
 * найдётся по телефону или заведётся сразу; повтор той же формы в окне
 * защиты откроет уже принятую заявку.
 */
export function NewLeadModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const sources = useLeadSources();
  const events = useOpenEvents();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [eventId, setEventId] = useState('');
  const [message, setMessage] = useState('');

  const { submit, pending } = useM3Submit(
    () =>
      api.post<{ id: number; repeated: boolean; notice: string | null }>('/leads', {
        phone,
        name: name.trim() || null,
        sourceId,
        eventId: eventId || null,
        message: message.trim() || null,
      }),
    (r) => r.notice ?? 'Заявка создана',
    (r) => {
      onClose();
      navigate(`/crm/leads/${r.id}`);
    },
  );

  return (
    <Modal
      title="Новая заявка"
      size="wide"
      onClose={onClose}
      confirm={{ label: 'Создать заявку', onClick: submit, disabled: !phone.trim() || !sourceId || pending }}
    >
      <p>Заявка вручную — звонок, сообщение, встреча. Клиент найдётся по телефону или заведётся сразу.</p>
      <div className="row keep">
        <div className="col">
          <PhoneField label="Телефон" value={phone} onValue={setPhone} />
        </div>
        <div className="col">
          <TextField label="Имя" placeholder="как представился" value={name} onValue={setName} />
        </div>
      </div>
      <SectionLabel>Канал</SectionLabel>
      <OptionList
        value={sourceId}
        onChange={setSourceId}
        options={(sources.data ?? []).filter((s) => s.isActive).map((s) => ({ value: s.id, title: s.name }))}
      />
      <SectionLabel>Мероприятие</SectionLabel>
      <OptionList
        value={eventId}
        onChange={setEventId}
        options={[{ value: '', title: 'Пока не знаем' }, ...events.map((e) => ({ value: e.id, title: e.label }))]}
      />
      <TextAreaField label="Сообщение" placeholder="Что спрашивали" value={message} onValue={setMessage} />
    </Modal>
  );
}

/**
 * Квалифицировать (qualify в макете, ТЗ 6.2): подтвердить клиента
 * и выбрать мероприятия; на каждое — своя сделка или привязка к той,
 * что уже есть.
 */
export function QualifyModal({ lead, onClose }: { lead: LeadCard; onClose: () => void }) {
  const events = useOpenEvents();
  const c = lead.client;
  const needName = !c || !c.lastName;
  const [lastName, setLastName] = useState(c?.lastName ?? '');
  const [firstName, setFirstName] = useState(c?.firstName ?? lead.contactName?.split(/\s+/)[0] ?? '');
  const [middleName, setMiddleName] = useState('');
  const [city, setCity] = useState<CityValue | null>(c?.city ?? lead.city ?? null);
  const [picked, setPicked] = useState<string[]>(lead.event ? [lead.event.id] : []);

  const { submit, pending } = useM3Submit(
    () =>
      api.post<{ deals: { id: string; created: boolean }[] }>(`/leads/${lead.id}/qualify`, {
        eventIds: picked,
        ...(needName
          ? { client: { lastName, firstName, middleName: middleName.trim() || null, cityId: city?.id ?? null } }
          : {}),
      }),
    (r) => `Заявка сконвертирована: ${deals(r.deals.length)}`,
    onClose,
  );

  const dealOn = (eventId: string) => lead.clientDeals.find((d) => d.eventId === eventId);

  return (
    <Modal
      title="Квалифицировать заявку"
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Квалифицировать',
        onClick: submit,
        disabled: pending || !picked.length || (needName && (!lastName.trim() || !firstName.trim())),
      }}
    >
      <p>
        Подтвердите контакт и выберите мероприятия. На каждое мероприятие будет своя сделка; если сделка этого клиента
        уже есть — заявка привяжется к ней.
      </p>
      {needName ? (
        <>
          <div className="row keep">
            <div className="col">
              <TextField label="Фамилия" value={lastName} onValue={setLastName} />
            </div>
            <div className="col">
              <TextField label="Имя" value={firstName} onValue={setFirstName} />
            </div>
          </div>
          <div className="row keep">
            <div className="col">
              <TextField label="Отчество" placeholder="необязательно" value={middleName} onValue={setMiddleName} />
            </div>
            <div className="col">
              <CityField value={city} onChange={setCity} inModal />
            </div>
          </div>
        </>
      ) : (
        <Note>
          Номер найден в базе: <b>{c.fullName}</b>. Данные клиента заявка не меняет.
        </Note>
      )}
      <SectionLabel>Мероприятия</SectionLabel>
      <div className="olist">
        {events.map((e) => {
          const d = dealOn(e.id);
          return (
            <Option
              key={e.id}
              checked={picked.includes(e.id)}
              onToggle={() => setPicked((p) => (p.includes(e.id) ? p.filter((x) => x !== e.id) : [...p, e.id]))}
              title={e.label}
              description={
                d
                  ? d.stage === 'lost'
                    ? 'сделка проиграна — вернётся в работу'
                    : `сделка уже есть · «${stageTitle(d.stage)}» — заявка привяжется к ней`
                  : 'создастся сделка «Новая»'
              }
            />
          );
        })}
      </div>
      {!events.length ? <Note tone="warn">Нет опубликованных мероприятий с открытым приёмом заявок.</Note> : null}
    </Modal>
  );
}

/** Отказ по заявке: причина из справочника обязательна (ТЗ 6.9) */
export function RejectModal({ lead, onClose }: { lead: LeadCard; onClose: () => void }) {
  const reasons = useReasons('lead');
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const { submit, pending } = useM3Submit(
    () => api.post(`/leads/${lead.id}/reject`, { reasonId, comment: comment.trim() || null }),
    'Отказ оформлен',
    onClose,
  );
  return (
    <Modal
      title="Отказ по заявке"
      onClose={onClose}
      confirm={{ label: 'Оформить отказ', onClick: submit, disabled: !reasonId || pending }}
    >
      <p>Причина обязательна — из неё строится отчёт по потерям.</p>
      <OptionList
        value={reasonId}
        onChange={setReasonId}
        options={(reasons.data ?? []).filter((r) => r.isActive).map((r) => ({ value: r.id, title: r.name }))}
      />
      <TextAreaField label="Комментарий" placeholder="Подробности, необязательно" value={comment} onValue={setComment} />
    </Modal>
  );
}

/** Отметить дублем: со ссылкой на заявку или без (ТЗ 6.9) */
export function DuplicateModal({ lead, onClose }: { lead: LeadCard; onClose: () => void }) {
  const [of, setOf] = useState<string | null>(null);
  const { submit, pending } = useM3Submit(
    () => api.post(`/leads/${lead.id}/duplicate`, { ofLeadId: of || null }),
    'Заявка отмечена дублем',
    onClose,
  );
  return (
    <Modal
      title="Отметить дублем"
      size="wide"
      onClose={onClose}
      confirm={{ label: 'Отметить дублем', onClick: submit, disabled: of === null || pending }}
    >
      <p>
        Дубли не идут в конверсию. Укажите, дублем какой заявки она является — или оставьте без ссылки, если первой
        заявки в системе нет.
      </p>
      <OptionList
        value={of}
        onChange={setOf}
        options={[
          {
            value: '',
            title: 'Без ссылки на заявку',
            description: 'например, клиент написал повторно до перехода на систему',
          },
          ...lead.sameNumber.map((m) => ({
            value: String(m.id),
            title: `#${m.id} · ${m.source}`,
            description: `${fullDateTime(m.createdAt)} · ${LEAD_STATUS[m.status].label.toLowerCase()}`,
          })),
        ]}
      />
    </Modal>
  );
}
